/**
 * ULD常量定义
 * 包含ULD状态、位置类型、事件类型等常量
 */

// ULD状态枚举（与后端保持一致）
export const ULD_STATUS = {
    AVL: "AVL",    // Available 可用
    DAM: "DAM",    // Damaged 损坏
    SRV: "SRV",    // Serviceable with Restrictions 可用但受限
    UNS: "UNS",    // Unserviceable 不可用
    RIP: "RIP",    // Repair in Progress 维修中
    HLD: "HLD",    // Held/Quarantined 留置/隔离
    SLV: "SLV",    // Salvageable 可修复/待回收
    SCR: "SCR",    // Scrapped 已报废
    LOST: "LOST",  // Lost 丢失
};

// ULD状态显示名称映射
export const ULD_STATUS_NAMES = {
    [ULD_STATUS.AVL]: "可用",
    [ULD_STATUS.DAM]: "损坏",
    [ULD_STATUS.SRV]: "受限可用",
    [ULD_STATUS.UNS]: "不可用",
    [ULD_STATUS.RIP]: "维修中",
    [ULD_STATUS.HLD]: "留置/隔离",
    [ULD_STATUS.SLV]: "可修复/待回收",
    [ULD_STATUS.SCR]: "已报废",
    [ULD_STATUS.LOST]: "丢失",
};

// ULD状态颜色映射
export const ULD_STATUS_COLORS = {
    [ULD_STATUS.AVL]: "green",
    [ULD_STATUS.DAM]: "red",
    [ULD_STATUS.SRV]: "orange",
    [ULD_STATUS.UNS]: "red",
    [ULD_STATUS.RIP]: "blue",
    [ULD_STATUS.HLD]: "purple",
    [ULD_STATUS.SLV]: "orange",
    [ULD_STATUS.SCR]: "volcano",
    [ULD_STATUS.LOST]: "red",
};

// ULD位置类型枚举
export const ULD_LOCATION_TYPE = {
    AIRPORT: "AIRPORT",
    FLIGHT: "FLIGHT",
    OTHER_LOCATION: "OTHER_LOCATION",
};

// ULD位置类型显示名称映射
export const ULD_LOCATION_TYPE_NAMES = {
    [ULD_LOCATION_TYPE.AIRPORT]: "机场",
    [ULD_LOCATION_TYPE.FLIGHT]: "航班",
    [ULD_LOCATION_TYPE.OTHER_LOCATION]: "其他位置",
};

// ULD所有权类型枚举
export const ULD_OWNERSHIP_TYPE = {
    OWNED: "自有",
    LEASED: "租赁",
};

// ULD所有权类型选项
export const ULD_OWNERSHIP_TYPE_OPTIONS = Object.keys(ULD_OWNERSHIP_TYPE).map(key => ({
    value: ULD_OWNERSHIP_TYPE[key],
    label: ULD_OWNERSHIP_TYPE[key]
}));

// ULD事件类型枚举
export const ULD_EVENT_TYPE = {
    LOC_CHANGE: "LOC_CHANGE",        // 位置变更
    STATUS_CHANGE: "STATUS_CHANGE",   // 状态变更
    TAG_ADD: "TAG_ADD",              // 添加标签
    TAG_REMOVE: "TAG_REMOVE",        // 移除标签
    TAG_CHANGE: "TAG_CHANGE",        // 标签变更（批量操作）
    HOLDER_UPDATE: "HOLDER_UPDATE",   // 持有者更新
    LOADED: "LOADED",                // 装载
    UNLOADED: "UNLOADED",            // 卸载
    CORRECTION: "CORRECTION",        // 纠错
    STOCK_IN: "STOCK_IN",            // 入库
};

// ULD事件类型显示名称映射
export const ULD_EVENT_TYPE_NAMES = {
    [ULD_EVENT_TYPE.LOC_CHANGE]: "位置变更",
    [ULD_EVENT_TYPE.STATUS_CHANGE]: "状态变更",
    [ULD_EVENT_TYPE.TAG_ADD]: "添加标签",
    [ULD_EVENT_TYPE.TAG_REMOVE]: "移除标签",
    [ULD_EVENT_TYPE.TAG_CHANGE]: "标签变更",
    [ULD_EVENT_TYPE.HOLDER_UPDATE]: "持有者更新",
    [ULD_EVENT_TYPE.LOADED]: "装载",
    [ULD_EVENT_TYPE.UNLOADED]: "卸载",
    [ULD_EVENT_TYPE.CORRECTION]: "纠错",
    [ULD_EVENT_TYPE.STOCK_IN]: "入库",
};

// ULD类型选项
export const ULD_TYPES = [
    "AKE", "AKH", "AKC", "AGA", "PMC", "PAG", "PAJ", "PYB", "PLA", "PGA"
];

// ULD类型详细选项（用于显示详细信息）
export const ULD_TYPE_OPTIONS = [
    { value: "AKE", label: "AKE - LD3集装箱" },
    { value: "AKH", label: "AKH - LD6集装箱" },
    { value: "AKC", label: "AKC - LD2集装箱" },
    { value: "AGA", label: "AGA - LD1集装箱" },
    { value: "PMC", label: "PMC - 货运板" },
    { value: "PAG", label: "PAG - 88x125板" },
    { value: "PAJ", label: "PAJ - 96x125板" },
    { value: "PYB", label: "PYB - 货运板" },
    { value: "PLA", label: "PLA - 货运板" },
    { value: "PGA", label: "PGA - 货运板" },
];

// ULD识别码校验正则表达式
export const ULD_IDENTIFIER_REGEX = /^[A-Z0-9]{3}[A-Z0-9]{7}$/;

// ULD识别码格式提示
export const ULD_IDENTIFIER_FORMAT_HINT = "格式：类型(3位)+序列号(7位)，总长度10位，如 AKE1234567";

// ULD状态选项（用于表单）
export const ULD_STATUS_OPTIONS = Object.keys(ULD_STATUS).map(key => ({
    value: ULD_STATUS[key],
    label: `${ULD_STATUS[key]} - ${ULD_STATUS_NAMES[ULD_STATUS[key]]}`
}));

// 新建ULD时的初始状态选项（按使用频率排序，适合新入库ULD的物理状态）
export const INITIAL_STATUS_OPTIONS = [
    { value: ULD_STATUS.AVL, label: `${ULD_STATUS.AVL} - ${ULD_STATUS_NAMES[ULD_STATUS.AVL]}（推荐：新ULD正常入库）` },
    { value: ULD_STATUS.SRV, label: `${ULD_STATUS.SRV} - ${ULD_STATUS_NAMES[ULD_STATUS.SRV]}（需要检查或有轻微限制）` },
    { value: ULD_STATUS.DAM, label: `${ULD_STATUS.DAM} - ${ULD_STATUS_NAMES[ULD_STATUS.DAM]}（到货时发现损坏）` },
    { value: ULD_STATUS.RIP, label: `${ULD_STATUS.RIP} - ${ULD_STATUS_NAMES[ULD_STATUS.RIP]}（需要立即维修）` },
    { value: ULD_STATUS.UNS, label: `${ULD_STATUS.UNS} - ${ULD_STATUS_NAMES[ULD_STATUS.UNS]}（暂时不可使用）` }
];

// ULD位置类型选项（用于表单）
export const ULD_LOCATION_TYPE_OPTIONS = Object.keys(ULD_LOCATION_TYPE).map(key => ({
    value: ULD_LOCATION_TYPE[key],
    label: ULD_LOCATION_TYPE_NAMES[ULD_LOCATION_TYPE[key]]
}));

// 创建模式选项
export const CREATE_MODE_OPTIONS = [
    { value: "single", label: "单个创建" },
    { value: "batch", label: "批量创建" }
];

// 可用状态集合（用于快速判断是否可用）
export const USABLE_STATUSES = new Set([
    ULD_STATUS.AVL,
    ULD_STATUS.SRV,
]);

// 不可用状态集合
export const UNUSABLE_STATUSES = new Set([
    ULD_STATUS.DAM,
    ULD_STATUS.UNS,
    ULD_STATUS.RIP,
    ULD_STATUS.HLD,
    ULD_STATUS.SLV,
    ULD_STATUS.SCR,
    ULD_STATUS.LOST
]);

// 默认分页参数
export const DEFAULT_PAGE_SIZE = 100;
export const DEFAULT_PAGE = 1;

// 表格列配置
export const ULD_TABLE_COLUMNS = {
    uld_identifier: "ULD识别码",
    uld_type: "类型",
    owner: "所有者",
    ownership_type: "所有权类型",
    current_status: "当前状态",
    current_location: "当前位置",
    last_moved_at: "最后移动时间",
    is_usable: "是否可用",
    tags: "标签",
    actions: "操作",
};

// 状态转换规则配置
export const STATUS_TRANSITION_CONFIG = {
    // 从可用状态可以转换到的状态
    [ULD_STATUS.AVL]: [ULD_STATUS.RIP, ULD_STATUS.UNS, ULD_STATUS.DAM, ULD_STATUS.HLD, ULD_STATUS.SCR, ULD_STATUS.LOST],
    
    // 从受限可用状态可以转换到的状态
    [ULD_STATUS.SRV]: [ULD_STATUS.AVL, ULD_STATUS.RIP, ULD_STATUS.UNS, ULD_STATUS.DAM, ULD_STATUS.HLD, ULD_STATUS.SCR],
    
    // 从损坏状态可以转换到的状态
    [ULD_STATUS.DAM]: [ULD_STATUS.RIP, ULD_STATUS.SLV, ULD_STATUS.SCR],
    
    // 从不可用状态可以转换到的状态
    [ULD_STATUS.UNS]: [ULD_STATUS.AVL, ULD_STATUS.SRV, ULD_STATUS.RIP, ULD_STATUS.SLV, ULD_STATUS.SCR],
    
    // 从维修中状态可以转换到的状态
    [ULD_STATUS.RIP]: [ULD_STATUS.AVL, ULD_STATUS.SRV, ULD_STATUS.UNS, ULD_STATUS.DAM, ULD_STATUS.SLV, ULD_STATUS.SCR],
    
    // 从留置状态可以转换到的状态
    [ULD_STATUS.HLD]: [ULD_STATUS.AVL, ULD_STATUS.SRV, ULD_STATUS.UNS, ULD_STATUS.SCR],
    
    // 从可修复状态可以转换到的状态
    [ULD_STATUS.SLV]: [ULD_STATUS.AVL, ULD_STATUS.SRV, ULD_STATUS.RIP, ULD_STATUS.SCR],
    
    // 报废和丢失状态通常是终态，但在特殊情况下可能需要纠错
    [ULD_STATUS.SCR]: [ULD_STATUS.AVL, ULD_STATUS.SRV], // 报废撤销
    [ULD_STATUS.LOST]: [ULD_STATUS.AVL, ULD_STATUS.SRV], // 找回丢失的ULD
};
